import React, { createContext, useCallback, useContext, useState } from 'react';
import apiService from '../services/api';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState({
    products: false,
    users: false,
    orders: false,
    categories: false,
    templates: false,
    coupons: false,
    dashboard: false,
    festiveSpecials: false,
    featuredTemplates: false,
    inhousePublications: false,
    hotSelling: false,
  });

  const invalidateCache = useCallback((endpoint) => {
    if (endpoint) {
      apiService.clearCacheForEndpoint(endpoint);
    } else {
      apiService.clearCache();
    }
  }, []);

  const refreshProducts = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, products: true }));
    try {
      invalidateCache('/admin/products');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, products: false }));
    }
  }, [invalidateCache]);

  const refreshUsers = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, users: true }));
    try {
      invalidateCache('/admin/users');
      invalidateCache('/admin/users/stats');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, users: false }));
    }
  }, [invalidateCache]);

  const refreshOrders = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, orders: true }));
    try {
      invalidateCache('/admin/orders');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, orders: false }));
    }
  }, [invalidateCache]);

  const refreshCategories = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, categories: true }));
    try {
      invalidateCache('/admin/categories');
      invalidateCache('/admin/categories/active');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, categories: false }));
    }
  }, [invalidateCache]);

  const refreshTemplates = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, templates: true }));
    try {
      invalidateCache('/admin/templates');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, templates: false }));
    }
  }, [invalidateCache]);

  const refreshCoupons = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, coupons: true }));
    try {
      invalidateCache('/admin/coupons');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, coupons: false }));
    }
  }, [invalidateCache]);

  const refreshDashboard = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, dashboard: true }));
    try {
      invalidateCache('/admin/dashboard/stats');
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, dashboard: false }));
    }
  }, [invalidateCache]);

  const refreshFestiveSpecials = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, festiveSpecials: true }));
    try {
      invalidateCache('/admin/festive-specials');
      invalidateCache('/products/festive-specials');
      if (window.clearHookCache) {
        window.clearHookCache('festive-specials');
      }
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, festiveSpecials: false }));
    }
  }, [invalidateCache]);

  const refreshFeaturedTemplates = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, featuredTemplates: true }));
    try {
      invalidateCache('/admin/featured-templates');
      invalidateCache('/templates/featured-landing');
      if (window.clearHookCache) {
        window.clearHookCache('featured-templates');
      }
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, featuredTemplates: false }));
    }
  }, [invalidateCache]);

  const refreshInhousePublications = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, inhousePublications: true }));
    try {
      invalidateCache('/admin/inhouse-publications');
      invalidateCache('/products/inhouse-publications');
      if (window.clearHookCache) {
        window.clearHookCache('inhouse-publications');
      }
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, inhousePublications: false }));
    }
  }, [invalidateCache]);

  const refreshHotSelling = useCallback(async () => {
    setRefreshing(prev => ({ ...prev, hotSelling: true }));
    try {
      invalidateCache('/admin/hot-selling');
      invalidateCache('/products/hot-selling');
      if (window.clearHookCache) {
        window.clearHookCache('hot-selling');
      }
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing(prev => ({ ...prev, hotSelling: false }));
    }
  }, [invalidateCache]);

  const refreshAll = useCallback(async () => {
    setRefreshing({
      products: true,
      users: true,
      orders: true,
      categories: true,
      templates: true,
      coupons: true,
      dashboard: true,
      festiveSpecials: true,
      featuredTemplates: true,
      inhousePublications: true,
      hotSelling: true,
    });
    try {
      apiService.clearCache();
      if (window.clearAllHookCache) {
        window.clearAllHookCache();
      }
      setRefreshKey(prev => prev + 1);
    } finally {
      setRefreshing({
        products: false,
        users: false,
        orders: false,
        categories: false,
        templates: false,
        coupons: false,
        dashboard: false,
        festiveSpecials: false,
        featuredTemplates: false,
        inhousePublications: false,
        hotSelling: false,
      });
    }
  }, []);

  const value = {
    refreshKey,
    refreshing,
    refreshProducts,
    refreshUsers,
    refreshOrders,
    refreshCategories,
    refreshTemplates,
    refreshCoupons,
    refreshDashboard,
    refreshFestiveSpecials,
    refreshFeaturedTemplates,
    refreshInhousePublications,
    refreshHotSelling,
    refreshAll,
    invalidateCache,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

